import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../lib/LangContext";
import { UI } from "../lib/content";
import { TID } from "../lib/testIds";

export default function Flipbook({ chapters }) {
    const { lang } = useLang();
    const t = UI[lang];
    const [i, setI] = useState(0);
    const total = chapters.length;

    const go = useCallback(
        (dir) => {
            setI((prev) => Math.min(Math.max(prev + dir, 0), total - 1));
        },
        [total]
    );

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") go(1);
            else if (e.key === "ArrowLeft") go(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [go]);

    // Scroll to top of container on chapter change
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [i]);

    const Current = chapters[i].Component;

    return (
        <div className="grain relative" data-testid="flipbook-container">
            <div className="pt-[62px] md:pt-[70px]">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={chapters[i].slug}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.8, ease: [0.7, 0, 0.2, 1] }}
                    >
                        <Current onBegin={() => go(1)} />
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Flipbook controls */}
            <div className="fixed bottom-0 left-0 right-0 z-40 no-print" style={{ background: "rgba(244,239,230,0.9)", backdropFilter: "blur(10px)" }}>
                <div className="h-px w-full" style={{ background: "#C9A96A", opacity: 0.5 }} />
                <div className="flex items-center justify-between px-6 md:px-10 py-4">
                    <button
                        data-testid={TID.flipbookPrev}
                        onClick={() => go(-1)}
                        disabled={i === 0}
                        className="font-micro text-[10px] md:text-xs flex items-center gap-3 disabled:opacity-30"
                        style={{ letterSpacing: "0.4em" }}
                    >
                        <span>←</span>
                        <span className="link-ed">{t.prev.toUpperCase()}</span>
                    </button>

                    <div data-testid={TID.flipbookCounter} className="font-didone italic text-lg md:text-xl" style={{ color: "#0B0B0B" }}>
                        <span>{String(i + 1).padStart(2, "0")}</span>
                        <span className="mx-3" style={{ color: "#C9A96A" }}>/</span>
                        <span className="opacity-60">{String(total).padStart(2, "0")}</span>
                    </div>

                    <button
                        data-testid={TID.flipbookNext}
                        onClick={() => go(1)}
                        disabled={i === total - 1}
                        className="font-micro text-[10px] md:text-xs flex items-center gap-3 disabled:opacity-30"
                        style={{ letterSpacing: "0.4em" }}
                    >
                        <span className="link-ed">{t.next.toUpperCase()}</span>
                        <span>→</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
